import { useMemo } from "react"
import { useSelector } from "react-redux"

import { initialSnippetSettings } from "rtk-slice"

import options from "utils/options"

import { useTheme } from "@mui/material"

import { oneDark } from "@codemirror/theme-one-dark"
import { EditorView } from "@codemirror/view"
import { monokai } from "@uiw/codemirror-theme-monokai"
import {
  androidstudio,
  atomone,
  aura,
  bbedit,
  bespin,
  darcula,
  dracula,
  duotoneDark,
  duotoneLight,
  eclipse,
  githubDark,
  githubLight,
  gruvboxDark,
  gruvboxLight,
  materialDark,
  materialLight,
  noctisLilac,
  nord,
  okaidia,
  solarizedDark,
  solarizedLight,
  sublime,
  tokyoNight,
  tokyoNightDay,
  tokyoNightStorm,
  vscodeDark,
  xcodeDark,
  xcodeLight,
} from "@uiw/codemirror-themes-all"

import {
  simona,
  snippifyDark,
  snippifyLight,
} from "../styles/cmThemes"

const useEditorSettings = (props) => {
  const { id } = props
  const {
    palette: {
      primary: { main },
    },
  } = useTheme()
  const initialSnippetSettingsState = useSelector(
    initialSnippetSettings
  )

  const isExistingSnippet = useSelector((state) =>
    state.snippets.snippets.find((snippet) => snippet.id === id)
  )
  const { body: snippetBody, type } = id ? isExistingSnippet : false
  const {
    settings: {
      editorSettings: {
        lineNumbers,
        lineWrapping,
        guttersBoxShadow,
        foldGutter,
        fullscreen,
        fontSize,
        lineHeight,
        foldGutterWidth,
        guttersPaddingLeft,
        contentPadding,
        tabSize,
        cursor,
        theme,
      },
    },
  } = id
    ? isExistingSnippet
    : { settings: initialSnippetSettingsState }

  const themes = useMemo(
    () => [
      { title: "Xcode Light", theme: xcodeLight },
      { title: "Xcode Dark", theme: xcodeDark },
      { title: "VSCode Dark", theme: vscodeDark },
      { title: "Tokyo Night Day", theme: tokyoNightDay },
      { title: "Tokyo Night Storm", theme: tokyoNightStorm },
      { title: "Tokyo Night", theme: tokyoNight },
      { title: "Sublime", theme: sublime },
      { title: "Solarized Light", theme: solarizedLight },
      { title: "Solarized Dark", theme: solarizedDark },
      { title: "Snippify Dark", theme: snippifyDark },
      { title: "Snippify Light", theme: snippifyLight },
      { title: "Okaidia", theme: okaidia },
      { title: "One Dark", theme: oneDark },
      { title: "Nord", theme: nord },
      { title: "Noctis Lilac", theme: noctisLilac },
      { title: "Material Dark", theme: materialDark },
      { title: "MonoKai", theme: monokai },
      { title: "Material Light", theme: materialLight },
      { title: "Gruvbox Dark", theme: gruvboxDark },
      { title: "Gruvbox Light", theme: gruvboxLight },
      { title: "Github Light", theme: githubLight },
      { title: "Github Dark", theme: githubDark },
      { title: "Eclipse", theme: eclipse },
      { title: "Duotone Light", theme: duotoneLight },
      { title: "Duotone Dark", theme: duotoneDark },
      { title: "Darcula", theme: darcula },
      { title: "Dracula", theme: dracula },
      { title: "Bespin", theme: bespin },
      { title: "BBedit", theme: bbedit },
      { title: "Aura", theme: aura },
      { title: "Atom One", theme: atomone },
      { title: "Android Studio", theme: androidstudio },
      { title: "Simona Dark", theme: simona },
    ],
    []
  )

  const basicSetup = {
    indentOnInput: true,
    lineNumbers: lineNumbers,
    autoCloseBrackets: true,
    autocompletion: true,
    allowMultipleSelections: true,
    autoCloseTags: true,
    syntaxHighlighting: true,
    highlightActiveLine: fullscreen ? true : false,
    highlightActiveLineGutter: fullscreen ? true : false,
    foldGutter: foldGutter,
    tabSize: tabSize,
  }

  const currentView = EditorView.theme({
    ".cm-theme": {
      overflowX: "hidden",
    },
    ".cm-editor": {
      overflowX: "hidden",
    },
    ".cm-scroller": {
      lineHeight: `${fontSize + lineHeight}px`,
      overflowX: "hidden",

      overflowY: "auto",
      minHeight: "350px",

      "& .cm-cursor": {
        borderLeftWidth:
          cursor === "Line"
            ? "3px"
            : cursor === "Block"
            ? "8px"
            : "1.2px !important",
      },
    },
    ".cm-gutter": {
      minHeight: fullscreen ? "100dvh" : "300px",
    },
    ".cm-lineNumbers": {
      width: fullscreen ? "4em" : "auto",
      color: "inherit",
      "& .cm-activeLineGutter": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: "bold",

        fontSize: `${fontSize + 2}px`,
        backgroundColor: "transparent !important",
      },
      "& .cm-activeLineGutter::before": {
        content: `""`,
        display: "inline-block !important",
        marginRight: "0.25em !important",
        width: "7px !important",
        height: "7px !important",
        borderRadius: "50%",
        backgroundColor: `${main} !important`,
      },
    },
    ".cm-gutterElement": {
      fontSize: `${fontSize}px`,
    },
    ".cm-content": {
      fontSize: `${fontSize}px`,
      padding: `${contentPadding.y}px ${contentPadding.x}px`,
      minHeight: fullscreen ? "100dvh" : "300px",
      overflowX: "hidden",
    },
    ".cm-activeLineGutter": {
      color: `${main} !important`,
      fontWeight: "bold !important",
    },

    ".cm-gutters": {
      boxShadow: guttersBoxShadow ? "-20px -60px 50px #000" : "none",
      paddingLeft: `${guttersPaddingLeft}px !important`,
      borderRight: "none",
    },
    ".cm-gutter.cm-foldGutter": {
      width: `${foldGutterWidth}px`,
      textAlign: "left",
      "& .cm-gutterElement": {
        fontSize: `${fontSize + 7}px !important`,
        backgroundColor: "transparent !important",
      },
    },
    ".cm-gutter.cm-lineNumbers": {
      "& .cm-gutterElement": {
        textAlign: "right",
        paddingRight: "1em",
      },
    },
  })

  const extensions = lineWrapping
    ? [EditorView.lineWrapping, currentView]
    : [currentView]

  const memoOptions = useMemo(() => options(themes), [themes])

  const currentThemeObj = useMemo(() => {
    const target = themes.filter((item) => item.title === theme)
    return target[0].theme
  }, [theme, themes])

  return {
    themes,
    theme,
    currentThemeObj,
    basicSetup,
    currentView,
    extensions,
    options: memoOptions,
    fullscreen,
    snippetBody,
    type,
  }
}
export default useEditorSettings
